import { useEffect, useMemo } from 'react';
import type { createBrowserRouter } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useApiService } from '../services/apiService';
import { createIdentitySync, initAnalytics, trackRouter, type GaIdentity } from '../utils/analytics';

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

// Renders nothing. Sends a scrubbed page_view on every route change (public
// pages included) and keeps the pseudonymous GA identity in step with the
// signed-in user and selected agency. Inert when no measurement id is built in.
export default function GoogleAnalytics({ router }: { router: ReturnType<typeof createBrowserRouter> }) {
  const enabled = useMemo(() => initAnalytics(MEASUREMENT_ID), []);
  const { user, selectedClientId } = useUser();
  const api = useApiService();

  useEffect(() => {
    if (!enabled) return;
    return trackRouter(router, window.location.origin, document.referrer);
  }, [enabled, router]);

  const syncIdentity = useMemo(
    () =>
      createIdentitySync(async (clientId) => {
        const response = await api.get<{ ga: GaIdentity | null }>('/api/analytics/identity', {
          params: clientId === null ? {} : { client_id: clientId },
        });
        return response.data?.ga ?? null;
      }),
    [api]
  );

  useEffect(() => {
    if (enabled) void syncIdentity(user?.id ?? null, selectedClientId);
  }, [enabled, syncIdentity, user?.id, selectedClientId]);

  return null;
}
